import { useEffect, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { scrollToId } from "../lib/scrollTo";

/**
 * SectionNav — floating dot navigation on the right (desktop) and a slim
 * top progress bar (mobile). Tracks the active section via IntersectionObserver.
 */
export default function SectionNav({ sections }) {
  const [active, setActive] = useState(sections[0]?.id);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  return (
    <>
      {/* Mobile: top progress bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[3px] bg-electric origin-left z-[70] md:hidden shadow-[0_0_12px_rgba(33,112,232,0.8)]"
        style={{ scaleX: progress }}
        data-testid="mobile-progress-bar"
      />

      {/* Desktop: floating dot nav */}
      <nav
        className="hidden md:flex fixed right-6 top-1/2 -translate-y-1/2 z-[70] flex-col items-end gap-3"
        aria-label="Navegación de secciones"
        data-testid="dot-nav"
      >
        {sections.map((s) => {
          const isActive = active === s.id;
          return (
            <button
              key={s.id}
              onClick={() => scrollToId(s.id)}
              className="group flex items-center gap-3 cursor-pointer"
              data-testid={`nav-dot-${s.id}`}
              aria-label={`Ir a ${s.label}`}
              aria-current={isActive ? "true" : undefined}
            >
              <span
                className={`font-poppins text-[11px] uppercase tracking-[0.2em] transition-all duration-300 ${
                  isActive
                    ? "opacity-100 text-white translate-x-0"
                    : "opacity-0 group-hover:opacity-70 text-ceramic translate-x-2 group-hover:translate-x-0"
                }`}
              >
                {s.label}
              </span>
              <span
                className={`rounded-full transition-all duration-300 ${
                  isActive
                    ? "w-3 h-3 bg-electric shadow-[0_0_12px_rgba(33,112,232,0.9)]"
                    : "w-2 h-2 bg-white/30 group-hover:bg-white/60"
                }`}
              />
            </button>
          );
        })}
      </nav>
    </>
  );
}
